import { AxiosResponse } from 'axios';
import { FunctionComponent, useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import DncTable from '@Components/DncTable';
import {
  ConfirmationMessageModal,
  MenuItem,
  TableActionsMenu,
  ScrollToTop,
  FullScreenLoader,
} from '@Components/index';
import { CommonConstants } from '@Constants/index';
import {
  getDncRows,
  getExportFilters,
  getDncListQuery,
} from '@Containers/Dnc/Dnc.utils';
import DncModal from '@Containers/Dnc/DncModal';
import { DncList } from '@Models/dnc.models';
import { useUpdatePersonalDncToResyncMutation } from '@Services/dataIntegration.service';
import {
  useGetDncListQuery,
  useRestoreDncRowMutation,
  useDeleteDncRowsMutation,
  useDeleteBulkDncRowsMutation,
} from '@Services/dnc.service';
import dncExportService from '@Services/dncExport.service';
import {
  BaseAlertProps,
  ConfirmationActionTypes,
  DialerCrudTypes,
  PhoneDispositions,
} from '@Typings/global.types';
import {
  downloadCsv,
  getConfirmationMessageNode,
  getModalBtnComponent,
  unformatUSNumber,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import { useTokenAccIdValueHook } from '@Utils/hooks/useTokenValues.hook';

const DNC: FunctionComponent = (): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { triggerAlert } = useAlert();

  /** PAGE HEADING */
  const heading = t('dnc.Do Not Call Numbers');

  /** STATES */
  const [checked, setChecked] = useState<readonly string[]>([]);
  const [modalCrudType, setModalCrudType] = useState<DialerCrudTypes | null>(
    null
  );
  const [dncId, setDncId] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationActionType, setConfirmationActionType] =
    useState<ConfirmationActionTypes | null>(null);
  const [isBulkAction, setIsBulkAction] = useState(false);

  /** Handle Query Data -->  */
  const DncListQuery: DncList = useMemo(
    () => getDncListQuery(query.parse(location.search)),
    [location.search]
  );
  /** <-- Handle Query Data   */

  /** RTK QUERY HOOK */
  const {
    data,
    isLoading,
    isFetching,
    error: dncRowsError,
  } = useGetDncListQuery(DncListQuery);
  const [restoreDncRow, restoreMethods] = useRestoreDncRowMutation();
  const [deleteDncRows, deleteRowsMethods] = useDeleteDncRowsMutation();
  const [deleteBulkRows, deleteBulkRowsMethods] =
    useDeleteBulkDncRowsMutation();
  const [updatePersonalDncToResync] = useUpdatePersonalDncToResyncMutation();

  const accountIdFromToken = useTokenAccIdValueHook() || 0;

  /** Table Loading State */
  const loading =
    isFetching ||
    restoreMethods.isLoading ||
    deleteRowsMethods.isLoading ||
    deleteBulkRowsMethods.isLoading ||
    exportLoading;

  /** Handle Error and Success Alert  -->
   */
  useEffect(() => {
    setIsBulkAction(false);
    setDncId('');
    setPhoneNumber('');

    // QUERY ERROR
    if (dncRowsError) {
      triggerAlert(dncRowsError, setAlertState);
    }
  }, [dncRowsError, triggerAlert]);

  useEffect(() => {
    // DELETE SUCCESS
    if (deleteRowsMethods.isSuccess) {
      triggerAlert(CommonConstants.GLOBAL_MESSAGES.DNC_DELETE, setAlertState);
      setChecked([]); // Reset Checked State
    }

    // DELETE ERROR
    if (deleteRowsMethods.error) {
      triggerAlert(deleteRowsMethods.error, setAlertState);
    }
  }, [deleteRowsMethods.isSuccess, deleteRowsMethods.error, triggerAlert]);
  /** <-- Handle Alert */

  /** Handle Error and Success Alert  -->
   * For BULK DELETE
   */
  useEffect(() => {
    // BULK DELETE SUCCESS
    if (deleteBulkRowsMethods.isSuccess) {
      setItemsCount(0); // Reset Items Count
      triggerAlert(CommonConstants.GLOBAL_MESSAGES.DNC_DELETE, setAlertState);
    }

    // BULK DELETE ERROR
    if (deleteBulkRowsMethods.error) {
      triggerAlert(deleteBulkRowsMethods.error, setAlertState);
    }
  }, [
    deleteBulkRowsMethods.isSuccess,
    deleteBulkRowsMethods.error,
    triggerAlert,
  ]);
  /** <-- Handle Alert */

  /** Handle Error and Success Alert  -->
   * For RESTORE
   */
  useEffect(() => {
    // RESTORE SUCCESS
    if (restoreMethods.isSuccess) {
      triggerAlert(CommonConstants.GLOBAL_MESSAGES.DNC_RESTORE, setAlertState);
    }

    // RESTORE ERROR
    if (restoreMethods.error) {
      triggerAlert(restoreMethods.error, setAlertState);
    }
  }, [restoreMethods.isSuccess, restoreMethods.error, triggerAlert]);
  /** <-- Handle Alert */

  /** DNC ACTION HANDLERS --> */
  /** HANDLES DNC NUMBER DELETE */
  const handleDelete = async (
    ids: readonly string[],
    phoneNumbers: string[]
  ) => {
    if (itemsCount) {
      return deleteBulkRows({ ...DncListQuery, itemsCount });
    }
    await deleteDncRows(ids);
    updatePersonalDncToResync({
      accountId: accountIdFromToken,
      phones: phoneNumbers,
      dispositionName: PhoneDispositions.NONE,
    }).catch((error) => {
      console.log('Error with deleting request to Resync service:', error);
    });
  };

  /** HANDLES DNC NUMBER DELETE */
  const handleRestore = async (id: string, phone: string) => {
    await restoreDncRow(id);
    updatePersonalDncToResync({
      accountId: accountIdFromToken,
      phones: [phone],
      dispositionName: PhoneDispositions.DO_NOT_CALL,
    }).catch((error) => {
      console.log('Error with restore request to Resync service:', error);
    });
  };

  /** HANDLES DNC ADD */
  const handleDncAdd = () => {
    setModalCrudType(DialerCrudTypes.Add);
    setDncId('');
    setPhoneNumber('');
  };
  /** <-- DNC ACTION HANDLERS */

  /** Table Modal Button Component Function
   *
   * Returns the Modal Button component which opens modal when clicked.
   */
  const modalBtnComponent = (
    id: string,
    phone: string,
    name: string,
    disabled: boolean
  ): JSX.Element => {
    const handleClick = () => {
      setModalCrudType(DialerCrudTypes.View);
      setDncId(id);
      setPhoneNumber(unformatUSNumber(phone));
    };

    return getModalBtnComponent({
      id,
      name,
      onClick: handleClick,
      isDisabled: disabled,
    });
  };

  /** Table Actions Component Function
   *
   * Returns the table actions component with required menu items.
   */
  const actionsComponent = (
    id: string,
    phone: string,
    disabled?: boolean
  ): JSX.Element => {
    const handleTableActionsClick = (
      confirmationType: ConfirmationActionTypes
    ) => {
      setIsConfirmationModalOpen(true);
      setConfirmationActionType(confirmationType);
      setDncId(id);
      setPhoneNumber(unformatUSNumber(phone));
    };

    /** Actions Menu Data for non Deleted Rows */
    const actionsMenu: MenuItem[] = [
      {
        label: t('common.messages.Edit'),
        icon: 'edit',
        value: id,
        onClick: () => {
          setModalCrudType(DialerCrudTypes.Edit);
          setDncId(id);
          setPhoneNumber(unformatUSNumber(phone));
        },
      },
      {
        label: t('common.messages.Delete'),
        icon: 'delete',
        value: id,
        onClick: () => {
          setDncId(id);
          setPhoneNumber(unformatUSNumber(phone));
          handleTableActionsClick(ConfirmationActionTypes.Delete);
        },
      },
    ];

    /** Actions Menu Data */
    const actionsMenuDeleted: MenuItem[] = [
      {
        label: t('common.actionsMenu.Restore'),
        icon: 'restore',
        value: id,
        onClick: () => {
          setDncId(id);
          setPhoneNumber(unformatUSNumber(phone));
          handleTableActionsClick(ConfirmationActionTypes.Restore);
        },
      },
    ];
    return (
      <TableActionsMenu
        menuItems={disabled ? actionsMenuDeleted : actionsMenu}
      />
    );
  };

  /** ROWS & TOTAL_ROWS_COUNT */
  const rows = getDncRows(data?.items, actionsComponent, modalBtnComponent);
  const totalRowsCount = data?.total || 0;
  const phonesNumberMapRef = useRef<{ [key: string]: string }>({});

  useEffect(() => {
    rows.forEach((item) => {
      const phone = item?.data?.phone;
      if (!phonesNumberMapRef.current[item.id]) {
        if (phone && typeof phone === 'object' && 'props' in phone) {
          phonesNumberMapRef.current[item.id] = unformatUSNumber(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            phone.props.text
          );
        }
      }
    });
  }, [rows]);

  const phonesNumberMap = phonesNumberMapRef.current;

  /** Handles confirmation click of modal and performs action accordingly */
  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    if (confirmationActionType === ConfirmationActionTypes.Delete) {
      return isBulkAction
        ? handleDelete(
            checked,
            checked.map((id) => phonesNumberMap[id])
          )
        : handleDelete([dncId], [phoneNumber]);
    }
    if (confirmationActionType === ConfirmationActionTypes.Restore) {
      return handleRestore(dncId, phoneNumber);
    }
  };

  /** Handle Data Exporting and export loading */
  const handleExportRows = (exportAll?: DncList) => {
    setExportLoading(true);

    // Add filters for exporting
    const exportMetaData =
      exportAll || getExportFilters(DncListQuery, checked, itemsCount);

    dncExportService
      .exportDncRows(exportMetaData, false)
      .then((res: AxiosResponse<string>) => {
        if (res.status === 200) {
          if (res.data) {
            downloadCsv(res.data, 'dnc');
            triggerAlert(
              CommonConstants.GLOBAL_MESSAGES.DNC_EXPORT,
              setAlertState,
              'success'
            );
          }
          setChecked([]);
        }
      })
      .catch(() => {
        triggerAlert('GENERIC_ERROR_MESSAGE', setAlertState, 'error');
      })
      .finally(() => {
        setExportLoading(false);
        setItemsCount(0);
      });
  };

  /** Handle Data Exporting and export loading for all records */
  const handleExportAll = () => {
    handleExportRows({ ...DncListQuery, itemsCount: totalRowsCount });
  };

  /** Handle Bulk Count
   * Callback to update ItemsCount
   */
  const handleBulk = (count: number) => setItemsCount(count);

  return !isLoading ? (
    <>
      <ScrollToTop
        dependencies={[
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) || '',
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) || '',
        ]}
      />
      <DncModal
        isGlobal={false}
        dncId={dncId}
        phoneNumber={phoneNumber}
        crudType={modalCrudType}
        setCrudType={setModalCrudType}
        deleteBtnOnClick={() => handleDelete([dncId], [phoneNumber])}
      />
      <ConfirmationMessageModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={getConfirmationMessageNode(confirmationActionType)}
        onClick={handleConfirmationModalClick}
      />
      <DncTable
        heading={
          query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) ? (
            <></>
          ) : (
            heading
          )
        }
        alertState={alertState}
        tabChild={!!query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID)}
        handleBulk={handleBulk}
        selectedBtnNumber={itemsCount || checked.length}
        deleteBtnProps={{
          onClick: () => {
            setConfirmationActionType(ConfirmationActionTypes.Delete);
            setIsConfirmationModalOpen(true);
            setIsBulkAction(true);
          },
        }}
        dncAddBtnClick={() => handleDncAdd()}
        exportBtnClick={() => handleExportRows()}
        exportAllClick={() => handleExportAll()}
        tableProps={{
          loading,
          rows,
          checked,
          setChecked,
          paginationProps: {
            count: totalRowsCount,
          },
        }}
      />
    </>
  ) : (
    <FullScreenLoader />
  );
};

export default DNC;
